import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Link2, ExternalLink, Edit, Volume2, FileText } from 'lucide-react'
import { EmptyState } from '@/components/ui/empty-state'
import { getDomain } from '@super/shared-types'
import type { Link, Pronunciation } from '@super/shared-types'
import { isMobileBreakpoint } from '@/lib/utils/window-utils'

interface LinkData {
  id: string
  url: string
  name?: string | null
  alias?: string | null
  pronunciation?: Pronunciation | null
  sendToCallers?: boolean
  domain: string
}

interface LinksTabProps {
  links: Link[]
  loading: boolean
  onLinkEdit: (link: Link) => void
  highlightedLinkId?: string | null
  onNavigateToPronunciation?: () => void
}

export function LinksTab({
  links,
  loading,
  onLinkEdit,
  highlightedLinkId,
  onNavigateToPronunciation,
}: LinksTabProps) {
  const linkData = useMemo((): LinkData[] => {
    return links
      .filter(link => link.id) // Filter out links without IDs
      .map(link => ({
        id: link.id!,
        url: link.url,
        name: link.name,
        alias: link.alias,
        pronunciation: link.pronunciation,
        sendToCallers: link.sendToCallers,
        domain: getDomain(link.url),
      }))
      .sort((a, b) => a.domain.localeCompare(b.domain))
  }, [links])

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px] hidden lg:table-cell">
                  Name
                </TableHead>
                <TableHead>URL</TableHead>
                <TableHead className="w-[140px] hidden lg:table-cell">
                  Domain
                </TableHead>
                <TableHead className="hidden lg:table-cell">
                  Description/Pronunciation
                </TableHead>
                <TableHead className="w-[80px] hidden lg:table-cell">
                  Send
                </TableHead>
                <TableHead
                  className="w-[120px] hidden lg:table-cell"
                  align="center"
                >
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  if (linkData.length === 0) {
    return (
      <EmptyState
        icon={<Link2 className="h-8 w-8" />}
        title="No knowledge base links"
        description="Drop a link in a knowledge base to see it populate in this&nbsp;table."
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                style={{ minWidth: '150px' }}
                className="hidden lg:table-cell"
              >
                Name
              </TableHead>
              <TableHead style={{ minWidth: '200px' }}>URL</TableHead>
              <TableHead
                style={{ minWidth: '140px' }}
                className="hidden lg:table-cell"
              >
                Domain
              </TableHead>
              <TableHead
                style={{ minWidth: '200px' }}
                className="hidden lg:table-cell max-w-0"
              >
                Description/Pronunciation
              </TableHead>
              <TableHead
                style={{ minWidth: '80px' }}
                className="hidden lg:table-cell"
              >
                Send
              </TableHead>
              <TableHead
                style={{ minWidth: '120px' }}
                className="hidden lg:table-cell"
                align="center"
              >
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {linkData.map((linkData, index) => {
              const fullLink = links.find(l => l.id === linkData.id)
              if (!fullLink) return null

              const isHighlighted = highlightedLinkId === linkData.id

              return (
                <TableRow
                  key={index}
                  className={`lg:cursor-default cursor-pointer lg:hover:bg-transparent hover:bg-muted/50 transition-colors ${
                    isHighlighted ? 'bg-muted' : ''
                  }`}
                  onClick={() => {
                    // Only handle click on mobile/tablet (when actions column is hidden)
                    if (isMobileBreakpoint()) {
                      onLinkEdit(fullLink)
                    }
                  }}
                >
                  <TableCell className="hidden lg:table-cell">
                    <span
                      className="text-xs truncate block"
                      title={linkData.name || undefined}
                    >
                      {linkData.name ? (
                        linkData.name
                      ) : (
                        <span className="text-muted-foreground italic">
                          No name
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="space-y-1">
                      <span
                        className="text-xs hover:text-blue-800 truncate block"
                        title={linkData.url}
                      >
                        {linkData.url}
                      </span>
                      {/* Show send state on mobile */}
                      <div className="lg:hidden">
                        <Badge
                          variant={
                            linkData.sendToCallers ? 'default' : 'secondary'
                          }
                          className="text-xs"
                        >
                          {linkData.sendToCallers ? 'Sending' : 'Not sending'}
                        </Badge>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex items-center gap-2">
                      <Link2 className="h-4 w-4 text-muted-foreground" />
                      <Badge variant="outline" className="text-xs">
                        {linkData.domain}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell max-w-0">
                    {linkData.alias ? (
                      <div
                        className="flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded min-w-0"
                        title={linkData.alias}
                      >
                        <FileText className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                        <span className="truncate">{linkData.alias}</span>
                      </div>
                    ) : linkData.pronunciation ? (
                      <button
                        onClick={e => {
                          e.stopPropagation()
                          onNavigateToPronunciation?.()
                        }}
                        className="flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded hover:bg-muted/80 transition-colors min-w-0 group w-full cursor-pointer"
                        title={`${linkData.pronunciation.phoneme} - Click to view in Pronunciation tab`}
                      >
                        <Volume2 className="h-3 w-3 flex-shrink-0 text-muted-foreground group-hover:text-foreground" />
                        <span className="truncate">
                          {linkData.pronunciation.phoneme}
                        </span>
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">
                        Not set
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Badge
                      variant={linkData.sendToCallers ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {linkData.sendToCallers ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex gap-1 justify-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onLinkEdit(fullLink)}
                        title="Edit pronunciation"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        title="Open in new tab"
                      >
                        <a
                          href={linkData.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
